/**
 * Icon Library for PDF Generation (Lucide React Integration)
 * Provides base64-encoded PNG icons using Lucide React components
 * 
 * Usage:
 * import { getIcon, getIconByTheme } from './iconLibrary';
 * 
 * // Get icon with specific theme and size
 * const calendarIcon = getIconByTheme('calendar', 'classic', 48);
 * pdf.addImage(calendarIcon, 'PNG', x, y, 48, 48);
 * 
 * // Get icon with custom color and size
 * const customIcon = await getIcon('arrow-right', { r: 100, g: 150, b: 200 }, 32);
 */

import React from 'react';
import { renderToString } from 'react-dom/server';
import * as LucideIcons from 'lucide-react';

/**
 * Theme color mappings (matches pdfGenerator.js)
 */
const THEME_COLORS = {
  classic: { r: 30, g: 58, b: 95 },
  warm: { r: 217, g: 119, b: 6 },
  nature: { r: 5, g: 150, b: 105 },
  elegant: { r: 124, g: 58, b: 237 },
  coral: { r: 255, g: 107, b: 107 },
  monochrome: { r: 55, g: 65, b: 81 }
};

/**
 * Lucide React icon component mapping
 * All icon names from Feather are direct matches in Lucide
 */
const LUCIDE_ICON_MAP = {
  'activity': LucideIcons?.Activity,
  'airplay': LucideIcons?.Airplay,
  'alert-circle': LucideIcons?.AlertCircle,
  'alert-octagon': LucideIcons?.AlertOctagon,
  'alert-triangle': LucideIcons?.AlertTriangle,
  'align-center': LucideIcons?.AlignCenter,
  'align-justify': LucideIcons?.AlignJustify,
  'align-left': LucideIcons?.AlignLeft,
  'align-right': LucideIcons?.AlignRight,
  'anchor': LucideIcons?.Anchor,
  'aperture': LucideIcons?.Aperture,
  'archive': LucideIcons?.Archive,
  'arrow-down-circle': LucideIcons?.ArrowDownCircle,
  'arrow-down-left': LucideIcons?.ArrowDownLeft,
  'arrow-down-right': LucideIcons?.ArrowDownRight,
  'arrow-down': LucideIcons?.ArrowDown,
  'arrow-left-circle': LucideIcons?.ArrowLeftCircle,
  'arrow-left': LucideIcons?.ArrowLeft,
  'arrow-right-circle': LucideIcons?.ArrowRightCircle,
  'arrow-right': LucideIcons?.ArrowRight,
  'arrow-up-circle': LucideIcons?.ArrowUpCircle,
  'arrow-up-left': LucideIcons?.ArrowUpLeft,
  'arrow-up-right': LucideIcons?.ArrowUpRight,
  'arrow-up': LucideIcons?.ArrowUp,
  'at-sign': LucideIcons?.AtSign,
  'award': LucideIcons?.Award,
  'bar-chart-2': LucideIcons?.BarChart2,
  'bar-chart': LucideIcons?.BarChart,
  'battery-charging': LucideIcons?.BatteryCharging,
  'battery': LucideIcons?.Battery,
  'bell-off': LucideIcons?.BellOff,
  'bell': LucideIcons?.Bell,
  'bluetooth': LucideIcons?.Bluetooth,
  'bold': LucideIcons?.Bold,
  'book-open': LucideIcons?.BookOpen,
  'book': LucideIcons?.Book,
  'bookmark': LucideIcons?.Bookmark,
  'box': LucideIcons?.Box,
  'briefcase': LucideIcons?.Briefcase,
  'calendar': LucideIcons?.Calendar,
  'camera-off': LucideIcons?.CameraOff,
  'camera': LucideIcons?.Camera,
  'cast': LucideIcons?.Cast,
  'check-circle': LucideIcons?.CheckCircle,
  'check-square': LucideIcons?.CheckSquare,
  'check': LucideIcons?.Check,
  'chevron-down': LucideIcons?.ChevronDown,
  'chevron-left': LucideIcons?.ChevronLeft,
  'chevron-right': LucideIcons?.ChevronRight,
  'chevrons-down': LucideIcons?.ChevronsDown,
  'chevrons-left': LucideIcons?.ChevronsLeft,
  'chevrons-right': LucideIcons?.ChevronsRight,
  'chevrons-up': LucideIcons?.ChevronsUp,
  'chrome': LucideIcons?.Chrome,
  'circle': LucideIcons?.Circle,
  'clipboard': LucideIcons?.Clipboard,
  'clipboard-check': LucideIcons?.ClipboardCheck,
  'clipboard-list': LucideIcons?.ClipboardList,
  'clock': LucideIcons?.Clock,
  'cloud-drizzle': LucideIcons?.CloudDrizzle,
  'cloud-lightning': LucideIcons?.CloudLightning,
  'cloud-off': LucideIcons?.CloudOff,
  'cloud-rain': LucideIcons?.CloudRain,
  'cloud-snow': LucideIcons?.CloudSnow,
  'cloud': LucideIcons?.Cloud,
  'code': LucideIcons?.Code,
  'codepen': LucideIcons?.Codepen,
  'codesandbox': LucideIcons?.Codesandbox,
  'coffee': LucideIcons?.Coffee,
  'columns': LucideIcons?.Columns,
  'command': LucideIcons?.Command,
  'compass': LucideIcons?.Compass,
  'copy': LucideIcons?.Copy,
  'corner-down-left': LucideIcons?.CornerDownLeft,
  'corner-down-right': LucideIcons?.CornerDownRight,
  'corner-left-down': LucideIcons?.CornerLeftDown,
  'corner-left-up': LucideIcons?.CornerLeftUp,
  'corner-right-down': LucideIcons?.CornerRightDown,
  'corner-right-up': LucideIcons?.CornerRightUp,
  'cpu': LucideIcons?.Cpu,
  'credit-card': LucideIcons?.CreditCard,
  'crop': LucideIcons?.Crop,
  'crosshair': LucideIcons?.Crosshair,
  'database': LucideIcons?.Database,
  'delete': LucideIcons?.Delete,
  'disc': LucideIcons?.Disc,
  'divide-circle': LucideIcons?.DivideCircle,
  'divide-square': LucideIcons?.DivideSquare,
  'divide': LucideIcons?.Divide,
  'dollar-sign': LucideIcons?.DollarSign,
  'download-cloud': LucideIcons?.DownloadCloud,
  'download': LucideIcons?.Download,
  'dribbble': LucideIcons?.Dribbble,
  'droplet': LucideIcons?.Droplet,
  'edit-2': LucideIcons?.Edit2,
  'edit-3': LucideIcons?.Edit3,
  'edit': LucideIcons?.Edit,
  'external-link': LucideIcons?.ExternalLink,
  'eye-off': LucideIcons?.EyeOff,
  'eye': LucideIcons?.Eye,
  'facebook': LucideIcons?.Facebook,
  'fast-forward': LucideIcons?.FastForward,
  'feather': LucideIcons?.Feather,
  'figma': LucideIcons?.Figma,
  'file-minus': LucideIcons?.FileMinus,
  'file-plus': LucideIcons?.FilePlus,
  'file-text': LucideIcons?.FileText,
  'file': LucideIcons?.File,
  'film': LucideIcons?.Film,
  'filter': LucideIcons?.Filter,
  'flag': LucideIcons?.Flag,
  'folder-minus': LucideIcons?.FolderMinus,
  'folder-plus': LucideIcons?.FolderPlus,
  'folder': LucideIcons?.Folder,
  'framer': LucideIcons?.Framer,
  'frown': LucideIcons?.Frown,
  'gift': LucideIcons?.Gift,
  'git-branch': LucideIcons?.GitBranch,
  'git-commit': LucideIcons?.GitCommit,
  'git-merge': LucideIcons?.GitMerge,
  'git-pull-request': LucideIcons?.GitPullRequest,
  'github': LucideIcons?.Github,
  'gitlab': LucideIcons?.Gitlab,
  'glass-water': LucideIcons?.GlassWater,
  'globe': LucideIcons?.Globe,
  'grid': LucideIcons?.Grid,
  'hard-drive': LucideIcons?.HardDrive,
  'hash': LucideIcons?.Hash,
  'headphones': LucideIcons?.Headphones,
  'heart': LucideIcons?.Heart,
  'help-circle': LucideIcons?.HelpCircle,
  'hexagon': LucideIcons?.Hexagon,
  'home': LucideIcons?.Home,
  'image': LucideIcons?.Image,
  'inbox': LucideIcons?.Inbox,
  'info': LucideIcons?.Info,
  'instagram': LucideIcons?.Instagram,
  'italic': LucideIcons?.Italic,
  'key': LucideIcons?.Key,
  'layers': LucideIcons?.Layers,
  'layout': LucideIcons?.Layout,
  'life-buoy': LucideIcons?.LifeBuoy,
  'link-2': LucideIcons?.Link2,
  'link': LucideIcons?.Link,
  'linkedin': LucideIcons?.Linkedin,
  'list': LucideIcons?.List,
  'loader': LucideIcons?.Loader,
  'lock': LucideIcons?.Lock,
  'log-in': LucideIcons?.LogIn,
  'log-out': LucideIcons?.LogOut,
  'mail': LucideIcons?.Mail,
  'map-pin': LucideIcons?.MapPin,
  'map': LucideIcons?.Map,
  'maximize-2': LucideIcons?.Maximize2,
  'maximize': LucideIcons?.Maximize,
  'meh': LucideIcons?.Meh,
  'menu': LucideIcons?.Menu,
  'message-circle': LucideIcons?.MessageCircle,
  'message-square': LucideIcons?.MessageSquare,
  'mic-off': LucideIcons?.MicOff,
  'mic': LucideIcons?.Mic,
  'minimize-2': LucideIcons?.Minimize2,
  'minimize': LucideIcons?.Minimize,
  'minus-circle': LucideIcons?.MinusCircle,
  'minus-square': LucideIcons?.MinusSquare,
  'minus': LucideIcons?.Minus,
  'monitor': LucideIcons?.Monitor,
  'moon': LucideIcons?.Moon,
  'more-horizontal': LucideIcons?.MoreHorizontal,
  'more-vertical': LucideIcons?.MoreVertical,
  'mouse-pointer': LucideIcons?.MousePointer,
  'move': LucideIcons?.Move,
  'music': LucideIcons?.Music,
  'navigation-2': LucideIcons?.Navigation2,
  'navigation': LucideIcons?.Navigation,
  'octagon': LucideIcons?.Octagon,
  'package': LucideIcons?.Package,
  'paperclip': LucideIcons?.Paperclip,
  'pause-circle': LucideIcons?.PauseCircle,
  'pause': LucideIcons?.Pause,
  'pen-tool': LucideIcons?.PenTool,
  'percent': LucideIcons?.Percent,
  'phone-call': LucideIcons?.PhoneCall,
  'phone-forwarded': LucideIcons?.PhoneForwarded,
  'target': LucideIcons?.Target,
  'user': LucideIcons?.User,
  'lightbulb': LucideIcons?.Lightbulb,
  'mood': LucideIcons?.Smile,
  'smile': LucideIcons?.Smile,
  'weather': LucideIcons?.Sun,
  'sun': LucideIcons?.Sun,
  'thermometer': LucideIcons?.Thermometer,
  'thermometer-sun': LucideIcons?.ThermometerSun,
  'thermometer-snowflake': LucideIcons?.ThermometerSnowflake,
  'sun-dim': LucideIcons?.SunDim,
  'cloud-drizzle': LucideIcons?.CloudDrizzle,
  'snowflake': LucideIcons?.Snowflake,
  'zap': LucideIcons?.Zap
};

/**
 * Converts SVG to PNG using Canvas with HIGH RESOLUTION
 * @param {string} svgString - SVG markup string
 * @param {number} width - Width in pixels
 * @param {number} height - Height in pixels
 * @returns {Promise<string>} Base64-encoded PNG data URL
 */
const svgToPng = async (svgString, width, height) => {
  return new Promise((resolve, reject) => {
    try {
      // Use 3x resolution for crisp rendering in PDF
      const scaleFactor = 3;
      const canvas = document.createElement('canvas');
      canvas.width = width * scaleFactor;
      canvas.height = height * scaleFactor;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Failed to get canvas 2D context'));
        return;
      }
      
      // Enable high-quality rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'low';
      
      const img = new Image();
      
      img.onerror = (error) => {
        console.error('SVG to Image conversion failed:', error);
        reject(new Error(`Failed to load SVG image: ${error}`));
      };
      
      img.onload = () => {
        try {
          ctx.drawImage(img, 0, 0, width * scaleFactor, height * scaleFactor);
          const pngDataUrl = canvas.toDataURL('image/png');
          
          if (!pngDataUrl || !pngDataUrl.startsWith('data:image/png;base64,')) {
            reject(new Error('Invalid PNG data URL generated from canvas'));
            return;
          }
          
          const base64Data = pngDataUrl.split(',')[1];
          if (!base64Data || base64Data.length === 0) {
            reject(new Error('Empty PNG data generated from canvas'));
            return;
          }
          
          resolve(pngDataUrl);
        } catch (drawError) {
          console.error('Canvas drawing failed:', drawError);
          reject(new Error(`Canvas drawing failed: ${drawError.message}`));
        }
      };
      
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      img.src = url;
      
      setTimeout(() => {
        URL.revokeObjectURL(url);
        reject(new Error('SVG to PNG conversion timeout after 5 seconds'));
      }, 5000);
      
    } catch (error) {
      console.error('SVG to PNG conversion setup failed:', error);
      reject(new Error(`SVG to PNG setup failed: ${error.message}`));
    }
  });
};

/**
 * Generate SVG string from Lucide React component
 * @param {string} iconName - Name of the icon
 * @param {Object} color - RGB color object {r, g, b}
 * @param {number} size - Icon size in pixels
 * @param {Object} bgColor - Optional background color
 * @returns {string} SVG string
 */
const generateLucideSvg = (iconName, color, size, bgColor) => {
  const LucideComponent = LUCIDE_ICON_MAP?.[iconName];
  
  if (!LucideComponent) {
    throw new Error(`Icon "${iconName}" not found in Lucide library`);
  }
  
  // Calculate proportional stroke width (capped at 2 for clarity)
  const proportionalStrokeWidth = (size / 24) * 2;
  const strokeWidth = Math.min(proportionalStrokeWidth, 2.2);
  
  const colorString = `rgb(${color?.r}, ${color?.g}, ${color?.b})`;
  
  // Render Lucide React component to SVG string
  let svgString = renderToString(
    React.createElement(LucideComponent, {
      size: size,
      color: colorString,
      strokeWidth: strokeWidth,
      fill: 'none'
    })
  );
  
  // Special handling for calendar icon with background color
  if (iconName === 'calendar' && bgColor) {
    // Add fill to calendar body rectangle for cover page watermark
    const bgColorString = `rgb(${bgColor?.r}, ${bgColor?.g}, ${bgColor?.b})`;
    svgString = svgString?.replace(
      /<rect([^>]*?)><\/rect>/,
      `<rect$1 fill="${bgColorString}"></rect>`
    );
  }
  
  return svgString;
};

/**
 * Generate icon with custom color and size
 * @param {string} iconName - Name of the icon from Lucide
 * @param {Object} color - RGB color object {r, g, b}
 * @param {number} size - Icon size in pixels (default: 24)
 * @param {Object} bgColor - Optional background color for filled icons
 * @returns {Promise<string>} Base64-encoded PNG data URL for use with pdf.addImage()
 */
export async function getIcon(iconName, color, size = 24, bgColor = null) {
  try {
    // Generate SVG from Lucide React component
    let svgString = generateLucideSvg(iconName, color, size, bgColor);
    
    if (!svgString || svgString?.length === 0) {
      throw new Error(`Failed to generate SVG for icon: ${iconName}`);
    }
    
    // Convert SVG to high-resolution PNG
    const pngDataUrl = await svgToPng(svgString, size, size);
    
    if (!pngDataUrl || !pngDataUrl?.startsWith('data:image/png;base64,')) {
      throw new Error(`Invalid PNG data URL for icon: ${iconName}`);
    }
    
    return pngDataUrl;
  } catch (error) {
    console.error(`Failed to generate icon "${iconName}":`, error);
    throw new Error(`Icon generation failed for ${iconName}: ${error.message}`);
  }
}

/**
 * Generate icon with theme color and size
 * @param {string} iconName - Name of the icon from Lucide
 * @param {string} theme - Theme name from THEME_COLORS
 * @param {number} size - Icon size in pixels (default: 24)
 * @param {Object} bgColor - Optional background color
 * @returns {Promise<string>} Base64-encoded PNG data URL for use with pdf.addImage()
 */
export async function getIconByTheme(iconName, theme, size = 24, bgColor = null) {
  const color = THEME_COLORS?.[theme];
  if (!color) {
    throw new Error(`Theme "${theme}" not found`);
  }
  return getIcon(iconName, color, size, bgColor);
}

/**
 * Get list of available icon names
 * @returns {string[]} Array of icon names
 */
export function getAvailableIcons() {
  return Object.keys(LUCIDE_ICON_MAP);
}

/**
 * Get list of available theme names
 * @returns {string[]} Array of theme names
 */
export function getAvailableThemes() {
  return Object.keys(THEME_COLORS);
}